import { useMemo, useState } from "react";

import { awaitingPayouts } from "../../utils/ledger";
import { formatRupees } from "../../utils/money";
import { PayoutRecordForm } from "./PayoutRecordForm";

function formatDay(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** "3 fees since 18 Sep 2026, 1 refund of Rs. 41.67" for one verifier. */
function summaryLine(group) {
  const fees = `${plural(group.feeCount, "fee")} since ${formatDay(group.firstEarnedAt)}`;
  if (group.refundCount === 0) return fees;
  return `${fees}, ${plural(group.refundCount, "refund")} of ${formatRupees(group.refundedPaise)}`;
}

/** What stands in for the button when refunds leave nothing to pay. */
function nothingToPay(group) {
  return group.netPaise < 0
    ? "Refunds exceed these earnings; the rest comes off the next payout."
    : "Nothing to pay: refunds cover these earnings.";
}

/**
 * The payouts still to make, one row per verifier, above the payouts
 * ledger. The admin pays each verifier by bank transfer, then records the
 * transfer here; the entries it covered are then marked paid out.
 */
export function AwaitingPayouts({ entries, onRecorded }) {
  const groups = useMemo(() => awaitingPayouts(entries), [entries]);
  const [openKey, setOpenKey] = useState(null);
  const [notice, setNotice] = useState(null);

  if (groups.length === 0 && !notice) return null;

  async function handleRecorded(group, result) {
    setOpenKey(null);
    setNotice(
      result.alreadyRecorded
        ? `This payout to ${group.verifierName} was already recorded.`
        : `Recorded ${formatRupees(result.netPaise)} paid to ${group.verifierName} on ${formatDay(result.paidOn)}, reference ${result.reference}.`,
    );
    await onRecorded();
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900">
          Awaiting payout
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Pay each verifier by bank transfer, then record the transfer here.
          Refunded fees are already taken off.
        </p>
      </div>
      {notice ? (
        <p
          role="status"
          className="px-4 py-2 text-xs font-medium text-emerald-800 bg-emerald-50 border-b border-emerald-100"
        >
          {notice}
        </p>
      ) : null}
      {groups.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-500">
          Every verifier has been paid.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {groups.map((group) => (
            <li key={group.key} className="px-4 py-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {group.verifierName}
                  </div>
                  <div className="text-xs text-slate-500">
                    {summaryLine(group)}
                    {group.contact ? ` - ${group.contact}` : ""}
                  </div>
                </div>
                <div className="text-sm font-bold text-slate-900 tabular-nums">
                  {formatRupees(Math.max(group.netPaise, 0))}
                </div>
                {group.netPaise > 0 ? (
                  <button
                    type="button"
                    onClick={() => setOpenKey(group.key)}
                    disabled={openKey === group.key}
                    className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    Mark paid out
                  </button>
                ) : (
                  <span className="text-xs text-slate-500 sm:max-w-56">
                    {nothingToPay(group)}
                  </span>
                )}
              </div>
              {openKey === group.key ? (
                <PayoutRecordForm
                  group={group}
                  onCancel={() => setOpenKey(null)}
                  onRecorded={(result) => handleRecorded(group, result)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
